<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'

const props = defineProps({
  isRecording: {
    type: Boolean,
    default: false,
  },
  isPaused: {
    type: Boolean,
    default: false,
  },
  isTranscribing: {
    type: Boolean,
    default: false,
  },
  durationLabel: {
    type: String,
    default: '00:00/15:00',
  },
  waveformData: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['discard', 'pause', 'resume'])

const liveCanvas = ref(null)
let liveDrawFrame = 0
let liveResizeObserver = null
let waveColor = ''

const isLive = computed(() => props.isRecording || props.isPaused)
const isVisible = computed(() => isLive.value || props.isTranscribing)

function stopLiveDrawLoop() {
  if (liveDrawFrame) {
    cancelAnimationFrame(liveDrawFrame)
    liveDrawFrame = 0
  }

  if (liveResizeObserver && typeof liveResizeObserver.disconnect === 'function') {
    liveResizeObserver.disconnect()
  }
  liveResizeObserver = null
}

function fitLiveCanvas(canvas) {
  const dpr = window.devicePixelRatio || 1
  const width = Math.max(1, Math.floor(canvas.clientWidth))
  const height = Math.max(1, Math.floor(canvas.clientHeight))
  const pixelWidth = Math.floor(width * dpr)
  const pixelHeight = Math.floor(height * dpr)

  if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
    canvas.width = pixelWidth
    canvas.height = pixelHeight
  }

  return { width, height, dpr }
}

function readWaveColor() {
  if (!waveColor && liveCanvas.value) {
    try {
      waveColor = getComputedStyle(liveCanvas.value).color || '#6366f1'
    }
    catch {
      waveColor = '#6366f1'
    }
  }

  return waveColor || '#6366f1'
}

function drawLiveWindow(context, width, height, values) {
  const barWidth = 2
  const gap = 1
  const stride = barWidth + gap
  const maxBars = Math.max(1, Math.floor(width / stride))
  const start = Math.max(0, values.length - maxBars)

  context.fillStyle = readWaveColor()

  for (let index = start; index < values.length; index += 1) {
    const value = values[index]
    const barHeight = value < 0
      ? 2
      : Math.max(2, Math.round((Number(value) / 255) * height))
    context.globalAlpha = value < 0 ? 0.35 : 1
    context.fillRect((index - start) * stride, height - barHeight, barWidth, barHeight)
  }
}

function drawWaveform() {
  const canvas = liveCanvas.value
  if (!canvas) {
    return
  }

  let context = null
  try {
    context = canvas.getContext('2d')
  }
  catch {
    return
  }

  if (!context) {
    return
  }

  const { width, height, dpr } = fitLiveCanvas(canvas)
  context.setTransform(dpr, 0, 0, dpr, 0, 0)
  context.clearRect(0, 0, width, height)

  const values = Array.isArray(props.waveformData) ? props.waveformData : []
  if (!values.length) {
    context.globalAlpha = 1
    return
  }

  drawLiveWindow(context, width, height, values)
  context.globalAlpha = 1
}

function startLiveDrawLoop() {
  stopLiveDrawLoop()
  waveColor = ''

  const tick = () => {
    drawWaveform()
    if (isLive.value) {
      liveDrawFrame = requestAnimationFrame(tick)
    }
  }

  liveDrawFrame = requestAnimationFrame(tick)

  if (typeof ResizeObserver === 'function' && liveCanvas.value) {
    const observer = new ResizeObserver(() => drawWaveform())
    if (typeof observer.observe === 'function') {
      liveResizeObserver = observer
      observer.observe(liveCanvas.value)
    }
  }
}

watch(
  isLive,
  async (live) => {
    stopLiveDrawLoop()
    if (!live) {
      return
    }
    await nextTick()
    startLiveDrawLoop()
  },
  { immediate: true },
)

onUnmounted(() => {
  stopLiveDrawLoop()
})
</script>

<template>
  <div
    v-if="isVisible"
    class="bradley-composer-recorder-preview bradley-composer-recorder-preview--inline"
    :class="{ 'bradley-composer-recorder-preview--transcribing': isTranscribing }"
  >
    <div
      v-if="isTranscribing"
      class="bradley-composer-recorder-preview__player"
    >
      <span
        class="bradley-composer-recorder-preview__spinner"
        aria-hidden="true"
      >
        <i class="pi pi-spinner animate-spin" />
      </span>
      <output class="bradley-composer-recorder-preview__status">
        {{ $t('newBradley.input.transcribingAudio') }}
      </output>
      <button
        type="button"
        class="bradley-composer-recorder-preview__icon-button"
        :title="$t('newBradley.input.cancelTranscription')"
        :aria-label="$t('newBradley.input.cancelTranscription')"
        @click="emit('discard')"
      >
        <i class="pi pi-times" />
      </button>
    </div>

    <div
      v-else
      class="bradley-composer-recorder-preview__player"
    >
      <button
        v-if="isPaused"
        type="button"
        class="bradley-composer-recorder-preview__icon-button"
        :title="$t('newBradley.input.resumeRecording')"
        :aria-label="$t('newBradley.input.resumeRecording')"
        @click="emit('resume')"
      >
        <i class="pi pi-play" />
      </button>
      <button
        v-else
        type="button"
        class="bradley-composer-recorder-preview__icon-button"
        :title="$t('newBradley.input.pauseRecording')"
        :aria-label="$t('newBradley.input.pauseRecording')"
        @click="emit('pause')"
      >
        <i class="pi pi-pause" />
      </button>

      <div
        class="bradley-composer-recorder-preview__wave"
        role="presentation"
        aria-hidden="true"
      >
        <canvas
          ref="liveCanvas"
          class="bradley-composer-recorder-preview__canvas"
        />
      </div>

      <span class="bradley-composer-recorder-preview__time">{{ durationLabel }}</span>

      <span
        class="bradley-composer-recorder-preview__dot"
        :class="{ 'bradley-composer-recorder-preview__dot--paused': isPaused }"
        aria-hidden="true"
      />
    </div>
  </div>
</template>

<style scoped>
.bradley-composer-recorder-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.bradley-composer-recorder-preview--inline {
  margin-bottom: 0;
  padding: 0.25rem 0;
  background: transparent;
  border: 0;
  border-radius: 0;
}

.bradley-composer-recorder-preview--inline .bradley-composer-recorder-preview__wave,
.bradley-composer-recorder-preview--inline .bradley-composer-recorder-preview__canvas {
  height: 40px;
}

.bradley-composer-recorder-preview__player {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.bradley-composer-recorder-preview__wave {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: flex-end;
  min-width: 0;
  height: 56px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.bradley-composer-recorder-preview__wave::-webkit-scrollbar {
  display: none;
}

.bradley-composer-recorder-preview__canvas {
  display: block;
  width: 100%;
  height: 56px;
  color: var(--color-dss-primary-500, #6366f1);
}

.bradley-composer-recorder-preview__dot {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: #dc2626;
}

.bradley-composer-recorder-preview__dot--paused {
  background: #9ca3af;
}

.bradley-composer-recorder-preview__time,
.bradley-composer-recorder-preview__status {
  flex: 0 0 auto;
  color: #374151;
  font-family: var(--font-inter, Inter, sans-serif);
  font-size: 0.8125rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.bradley-composer-recorder-preview__status {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bradley-composer-recorder-preview__spinner {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--color-dss-primary-500, #6366f1);
}

.bradley-composer-recorder-preview__spinner i {
  font-size: 0.95rem;
}

.bradley-composer-recorder-preview__icon-button {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  margin: 0;
  padding: 0;
  color: #4b5563;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
}

.bradley-composer-recorder-preview__icon-button:hover {
  background: #f3f4f6;
}

.bradley-composer-recorder-preview__icon-button i {
  font-size: 0.7rem;
}
</style>

<style>
html.dark .bradley-composer-recorder-preview__canvas,
html.dark .bradley-composer-recorder-preview__spinner {
  color: #ccccf5;
}

html.dark .bradley-composer-recorder-preview__time,
html.dark .bradley-composer-recorder-preview__status {
  color: #e0e0f9;
}

html.dark .bradley-composer-recorder-preview__icon-button {
  color: #c2c2e8;
  background: #14143a;
  border-color: #2e2f5a;
}

html.dark .bradley-composer-recorder-preview__icon-button:hover {
  background: #25274f;
}
</style>
